import goog from "@tastyworks/boulangerie-bundle"
import type { AdminMarketData, Position } from "@tastyworks/tastyworks-api"

const TwSymbolUtil = goog.module.get("com.dough.trade.util.TwSymbolUtil")
const SecurityFormatter = goog.module.get("com.dough.text.SecurityFormatter")

export class PositionRow {
  security: any

  constructor(
    readonly position: Position,
    readonly marketData: AdminMarketData | undefined
  ) {
    this.security = TwSymbolUtil.toSecurity(
      position.symbol,
      position.instrumentType
    )
  }

  get bid() {
    return formatQuotePrice(this.marketData?.bid)
  }

  get ask() {
    return formatQuotePrice(this.marketData?.ask)
  }

  get last() {
    return formatQuotePrice(this.marketData?.last)
  }

  get underlyingSymbol() {
    if (this.instrumentType === "Future") {
      return this.position.symbol
    }
    return this.position.underlyingSymbol
  }

  get instrumentType() {
    return this.position.instrumentType
  }

  get costPerUnit() {
    return {
      value: this.position.averageOpenPrice,
      direction: this.position.quantityDirection,
    }
  }

  get quantity() {
    return formatPositionQuantity(this.position)
  }

  get expirationDate() {
    if (this.instrumentType === "Future") {
      return this.position.expiresAt || null
    }
    return this.security.expirationDate
  }

  get daysToExpiration() {
    if (this.instrumentType === "Future") {
      return formatDaysToExpiration(this.position.expiresAt)
    }
    return SecurityFormatter.daysToExpiration(this.security)
  }

  get strikePrice() {
    return this.security.strikePrice
  }

  get callOrPut() {
    return this.security.callOrPut
  }

  get callPutStock() {
    switch (this.instrumentType) {
      case "Equity Option":
      case "Future Option":
        return this.security.simplifiedAssetType
      case "Equity":
        return "Shares"
      case "Future":
        return "Futures"
      default:
        return ""
    }
  }
}

const formatQuotePrice = (price: number | undefined): string =>
  !price || isNaN(price) ? "--" : `${price}`

const MS_PER_DAY = 24 * 60 * 60 * 1000

// Futures expirations follow per-product conventions — last Friday for /MBT,
// last business day for /10Y, third Friday for /ES — so the contract month in
// the symbol can't produce them. expires-at is the instrument's own value, and
// the strings here match what SecurityFormatter renders for options: "--" with
// no expiration, "Exp" once it has passed, and a day count that rounds a
// partial day up past 0.6 so both row types agree in the same DTE column.
const PARTIAL_DAY_ROUNDS_UP_ABOVE = 0.6

const formatDaysToExpiration = (expiresAt: string): string => {
  const expiration = new Date(expiresAt).getTime()
  if (isNaN(expiration)) return "--"
  const remaining = expiration - Date.now()
  if (remaining <= 0) return "Exp"
  const days = remaining / MS_PER_DAY
  const wholeDays = Math.floor(days)
  const partialDay = days - wholeDays
  return `${partialDay > PARTIAL_DAY_ROUNDS_UP_ABOVE ? wholeDays + 1 : wholeDays}d`
}

const getSign = (quantityDirection: string): string => {
  switch (quantityDirection) {
    case "Long":
      return "+"
    case "Short":
      return "-"
    default:
      return ""
  }
}

const formatPositionQuantity = (position: Position): string => {
  return getSign(position.quantityDirection) + position.quantity
}
